import { useState } from 'react';
import {
  Phone,
  MessageSquare,
  Mail,
  UserCheck,
  LayoutDashboard,
  Check,
  Clock,
  X,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { RetentionActionRow } from '@/hooks/use-retention-actions';

interface RetentionActionCardProps {
  action: RetentionActionRow;
  onComplete: (actionId: string, notes?: string) => void;
  onSnooze: (actionId: string, days: number) => void;
  onDismiss: (actionId: string, reason?: string) => void;
  onClientClick?: (clientId: string) => void;
  showClientName?: boolean;
}

const actionTypeIcons: Record<string, typeof Phone> = {
  call: Phone,
  sms: MessageSquare,
  email: Mail,
  in_person: UserCheck,
  in_app: LayoutDashboard,
};

const priorityStyles: Record<string, { bg: string; border: string; dot: string }> = {
  urgent: {
    bg: 'bg-destructive/10',
    border: 'border-destructive/20',
    dot: 'bg-destructive',
  },
  high: {
    bg: 'bg-warning/10',
    border: 'border-warning/20',
    dot: 'bg-warning',
  },
  medium: {
    bg: 'bg-warning/10',
    border: 'border-warning/20',
    dot: 'bg-warning',
  },
  low: {
    bg: 'bg-muted/50',
    border: 'border-border',
    dot: 'bg-muted-foreground',
  },
};

export function RetentionActionCard({
  action,
  onComplete,
  onSnooze,
  onDismiss,
  onClientClick,
  showClientName = false,
}: RetentionActionCardProps) {
  const [completing, setCompleting] = useState(false);
  const Icon = actionTypeIcons[action.action_type] || LayoutDashboard;
  const style = priorityStyles[action.priority] || priorityStyles.low;

  const handleComplete = async () => {
    setCompleting(true);
    await onComplete(action.id);
    setCompleting(false);
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border transition-colors',
        style.bg,
        style.border,
      )}
    >
      {/* Priority dot + action type icon */}
      <div className="flex flex-col items-center gap-1.5 pt-0.5">
        <div className={cn('h-2 w-2 rounded-full', style.dot)} />
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {showClientName && action.client_name && (
          <button
            onClick={() => onClientClick?.(action.client_id)}
            className="text-xs font-medium text-primary hover:underline mb-0.5 block"
          >
            {action.client_name}
          </button>
        )}
        <p className="text-sm leading-snug">{action.action_text}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-[11px] text-muted-foreground capitalize">
            {action.priority}
          </span>
          <span className="text-[11px] text-muted-foreground">·</span>
          <span className="text-[11px] text-muted-foreground capitalize">
            {action.action_type.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-success hover:text-success hover:bg-success/10"
          onClick={handleComplete}
          disabled={completing}
          title="Mark as done"
        >
          <Check className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              title="More options"
            >
              <Clock className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onSnooze(action.id, 1)}>
              Snooze 1 day
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSnooze(action.id, 3)}>
              Snooze 3 days
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSnooze(action.id, 7)}>
              Snooze 1 week
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-muted-foreground"
              onClick={() => onDismiss(action.id)}
            >
              <X className="h-3.5 w-3.5 mr-2" />
              Dismiss
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {showClientName && onClientClick && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={() => onClientClick(action.client_id)}
            title="View client"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
